import { useLazyQuery } from '@apollo/client';
import { Empty, Select, Spin } from 'antd';
import { debounce, uniqBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { LIMIT, SORT } from '../common/constants';
import { PINCODES } from './graphql/Queries';
import SelectComponent from './SelectComponent';

const { Option } = Select;

const LOADING_OPTION_KEY = '__loading__';

const PincodeSelectName = (props) => {
  const { userData, ...rest } = props;
  const [pincodeList, setPincodeList] = useState([]);
  const [pincodesSearchTerm, setPincodesSearchTerm] = useState('');
  const [isAllPincodesFetched, setIsAllPincodesFetched] = useState(false);
  const [isPincodesAllowClear, setIsPincodesAllowClear] = useState(false);
  const [isPincodesDropdownVisible, setIsPincodesDropdownVisible] = useState(
    false,
  );
  const [isFetchMorePincodesLoading, setIsFetchMorePincodesLoading] = useState(
    false,
  );
  const listRef = useRef(null);
  const isFetchingMoreRef = useRef(false);

  const [pincodes, { loading: isPincodesLoading }] = useLazyQuery(PINCODES, {
    onCompleted: (response) => {
      if (response?.pincodes?.data?.length < LIMIT) {
        setIsAllPincodesFetched(true);
      }
      isFetchingMoreRef.current = false;
      setIsFetchMorePincodesLoading(false);
      if (userData?.pincode) {
        setPincodeList(
          uniqBy(
            [userData?.pincode, ...pincodeList, ...response?.pincodes?.data],
            'id',
          ),
        );
        return;
      }
      setPincodeList([...pincodeList, ...response?.pincodes?.data]);
    },
    fetchPolicy: 'network-only',
    onError() {
      isFetchingMoreRef.current = false;
      setIsFetchMorePincodesLoading(false);
    },
  });

  useEffect(() => {
    setPincodesSearchTerm('');
    setPincodeList([]);
    pincodes({
      variables: {
        filter: {
          limit: LIMIT,
          skip: 0,
          search: pincodesSearchTerm,
        },
        sort: {
          field: 'pincode',
          order: SORT?.ASC,
        },
      },
    });
    setIsAllPincodesFetched(false);
  }, [isPincodesDropdownVisible]);

  const handleCategoriesScroll = (event) => {
    if (
      !isAllPincodesFetched &&
      !isPincodesLoading &&
      !isFetchingMoreRef.current
    ) {
      const target = event?.target;
      const currentLength = pincodeList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 20
      ) {
        isFetchingMoreRef.current = true;
        setIsFetchMorePincodesLoading(true);
        pincodes({
          variables: {
            filter: {
              limit: LIMIT,
              skip: currentLength,
              search: pincodesSearchTerm,
            },
            sort: {
              field: 'pincode',
              order: SORT?.ASC,
            },
          },
        });
      }
    }
  };

  const handleCategoriesSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();
    setIsAllPincodesFetched(false);
    setPincodeList([]);
    setPincodesSearchTerm(trimSearch);
    pincodes({
      variables: {
        filter: {
          limit: LIMIT,
          skip: 0,
          search: trimSearch,
        },
        sort: {
          field: 'pincode',
          order: SORT?.ASC,
        },
      },
    });
  };

  const handleSearchCategoriesDebounce = debounce(handleCategoriesSearch, 500);

  useEffect(() => {
    handleSearchCategoriesDebounce?.cancel();
  }, [handleSearchCategoriesDebounce]);

  useEffect(() => {
    if (isPincodesLoading) {
      setIsPincodesAllowClear(false);
    } else {
      setIsPincodesAllowClear(true);
    }
  }, [isPincodesLoading]);

  useEffect(() => {
    if (!isFetchMorePincodesLoading || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isFetchMorePincodesLoading]);

  return (
    <SelectComponent
      showSearch
      loading={isPincodesLoading && isPincodesDropdownVisible}
      placeholder="Select Pincode"
      optionFilterProp="children"
      allowClear={isPincodesAllowClear}
      onPopupScroll={(event) => {
        listRef.current = event?.target;
        handleCategoriesScroll(event);
      }}
      onSearch={handleSearchCategoriesDebounce}
      onDropdownVisibleChange={(visible) =>
        setIsPincodesDropdownVisible(visible)
      }
      filterOption={false}
      notFoundContent={
        !isFetchMorePincodesLoading && isPincodesLoading ? (
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        ) : (
          <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      {...rest}
    >
      {pincodeList?.map((pincode) => (
        <Option key={pincode?.id} value={pincode?.pincode}>
          {pincode?.pincode}
        </Option>
      ))}
      {isFetchMorePincodesLoading && (
        <Option key={LOADING_OPTION_KEY} value={LOADING_OPTION_KEY} disabled>
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        </Option>
      )}
    </SelectComponent>
  );
};

export default PincodeSelectName;
